export type GameLoopCallbacks = {
  update: (dt: number) => void; // fixed timestep game logic
  render: (alpha: number) => void; // variable timestep rendering
};

export type GameLoop = {
  start: () => void;
  stop: () => void;
  isRunning: boolean;
};

const TICK_RATE = 1000 / 60; // 60 logic ticks per second
const MAX_DELTA = 250; // prevent spiral of death

export function createGameLoop(callbacks: GameLoopCallbacks): GameLoop {
  let animFrameId: number | null = null;
  let lastTime = 0;
  let accumulator = 0;
  let isRunning = false;

  function loop(currentTime: number) {
    if (!isRunning) return;

    let deltaTime = currentTime - lastTime;
    lastTime = currentTime;

    // Clamp to prevent spiral of death after tab switch
    if (deltaTime > MAX_DELTA) deltaTime = MAX_DELTA;

    accumulator += deltaTime;

    // Fixed timestep updates
    while (accumulator >= TICK_RATE) {
      callbacks.update(TICK_RATE / 1000);
      accumulator -= TICK_RATE;
    }

    // Render with interpolation alpha
    const alpha = accumulator / TICK_RATE;
    callbacks.render(alpha);

    animFrameId = requestAnimationFrame(loop);
  }

  return {
    start() {
      if (isRunning) return;
      isRunning = true;
      lastTime = performance.now();
      accumulator = 0;
      animFrameId = requestAnimationFrame(loop);
    },
    stop() {
      isRunning = false;
      if (animFrameId !== null) {
        cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
    },
    get isRunning() {
      return isRunning;
    },
  };
}
